import {
  Cluster,
  ResolvedSubscription,
  SubscriptionPlan,
  UpgradePlan,
  UpgradeTemplate,
} from 'api/models';
import {
  addMinutes,
  differenceInDays,
  differenceInMonths,
  differenceInYears,
  endOfMonth,
  format,
  fromUnixTime,
  getUnixTime,
  isBefore,
  subMonths,
} from 'date-fns';
import { getDateFromUnixTimestamp } from './formatTime';
import { isEmpty } from 'lodash';
import {
  filterUpgradePlansByResources,
  filterUpgradeTemplatesByResources,
} from './upgrades';

// This method checks if the first month has passed since the user created their first cluster.
// The month is considered to have passed if the last date of the previous month has passed regardless of the day of the month.
export const isFirstMonthPassedSinceOnboarded = (clusters: Cluster[]) => {
  const lastDateOfPreviousMonth = endOfMonth(subMonths(new Date(), 1));
  const lastTimestampOfPreviousMonth = getUnixTime(lastDateOfPreviousMonth);

  return clusters.some(
    (cluster) =>
      cluster.internal_k8s_ref !== 'example-cluster-ref' &&
      cluster.created < lastTimestampOfPreviousMonth,
  );
};

export function getMostRelevanSubscription(
  subs: ResolvedSubscription[] | undefined,
): ResolvedSubscription | undefined {
  if (subs === undefined || subs.length === 0) return undefined;

  return (subs || []).sort(
    (a, b) =>
      (b.expiry || Number.MAX_SAFE_INTEGER) -
      (a.expiry || Number.MAX_SAFE_INTEGER),
  )[0];
}

export function getHumanReadableDate(
  unixTimestamp: number | undefined, //unix time
): string {
  if (!unixTimestamp) return '-';

  const date = getDateFromUnixTimestamp(unixTimestamp);
  return date ? format(addMinutes(date, date.getTimezoneOffset()), 'PP') : '-';
}

export function showUpdateNow(subscriptionPlan: SubscriptionPlan | undefined) {
  if (
    subscriptionPlan &&
    ['FreeTrial', 'GuidedPOC', 'Demo', 'POCv2'].includes(subscriptionPlan)
  )
    return true;
  else return false;
}

export function isSubscriptionPlan(plan: string | undefined) {
  return Object.values(SubscriptionPlan).includes(plan as SubscriptionPlan);
}

function isPOC(plan: string | undefined) {
  return plan === SubscriptionPlan.GuidedPOC || plan === SubscriptionPlan.POCv2;
}

export function canRequestPlanExtension(subscription?: ResolvedSubscription) {
  return (
    !isEmpty(subscription) &&
    isPOC(subscription?.plan) &&
    (getExpiryInDays(subscription?.expiry) || 0) <= 14
  );
}

export function getExpiryInText(subscriptionPlan: SubscriptionPlan) {
  if (subscriptionPlan === undefined) return '';
  if (['Starter', 'Business', 'Enterprise'].includes(subscriptionPlan))
    return 'Renews';
  else return 'Ends';
}

export function getExpiryInDays(
  unixTimeStamp: number | undefined,
): number | undefined {
  const date = getDateFromUnixTimestamp(unixTimeStamp);

  if (!unixTimeStamp || !date) {
    return undefined;
  }
  const todayUTC = new Date(
    Date.UTC(
      new Date().getUTCFullYear(),
      new Date().getUTCMonth(),
      new Date().getUTCDate(),
    ),
  );

  const expiryUTC = new Date(
    Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
  );

  return differenceInDays(expiryUTC, todayUTC);
}

export function getExpiryInDaysText(unixTimeStamp: number | undefined): string {
  let expiryDays = getExpiryInDays(unixTimeStamp);
  if (expiryDays === 0) return 'Expires soon';
  else if (expiryDays && expiryDays > 0)
    return `${
      expiryDays > 1 ? `${expiryDays} days` : `${expiryDays} day`
    } remaining`;
  else if (expiryDays && expiryDays < 0) return ExpiryDisplay(unixTimeStamp);
  else return '';
}

export function getHeaderExpiryInDaysText(
  unixTimeStamp: number | undefined,
  subscriptionPlan: string | undefined,
): string {
  let expiryDays = getExpiryInDays(unixTimeStamp);
  if (expiryDays === 0) return `Your ${subscriptionPlan} Plan expires today.`;
  else if (expiryDays && expiryDays > 0)
    return `${
      expiryDays > 1 ? `${expiryDays} days` : `${expiryDays} day`
    } left in your ${subscriptionPlan} Plan.`;
  else if (expiryDays && expiryDays < 0)
    return `Your ${subscriptionPlan} Plan ${ExpiryDisplay(unixTimeStamp)}.`;
  else return '';
}

export function getAlertBannerExpiryInDaysText(
  unixTimeStamp: number | undefined,
  subscriptionPlan: string | undefined,
): string {
  if (unixTimeStamp === undefined)
    return `You are currently using the ${subscriptionPlan} Plan`;
  let expiryDays = getExpiryInDays(unixTimeStamp);
  if (expiryDays === 0) return `Your ${subscriptionPlan} Plan expires today`;
  else if (expiryDays && expiryDays > 0)
    return `Your ${subscriptionPlan} Plan ends in ${
      expiryDays > 1 ? `${expiryDays} days` : `${expiryDays} day`
    }`;
  else if (expiryDays && expiryDays < 0)
    return `Your ${subscriptionPlan} Plan ${ExpiryDisplay(unixTimeStamp)}.`;
  else return '';
}

export function getPlanExpiryState(
  expiryDate: number | undefined, //unix time
): 'default' | 'warning' | 'error' | 'expired' | null {
  const earliestExpiry = getExpiryInDays(expiryDate);
  if (earliestExpiry === undefined) {
    return null;
  } else if (earliestExpiry > 31) {
    return 'default';
  } else if (earliestExpiry > 7) {
    return 'warning';
  } else if (earliestExpiry < 0) {
    return 'expired';
  } else return 'error';
}

export const getNodeUsageCount = (activeClusters: Cluster[] | undefined) => {
  if (activeClusters === undefined) return undefined;
  else
    return activeClusters.reduce(
      (accumulator, cluster) => accumulator + cluster.node_count,
      0,
    );
};

export const getProgressValue = (
  usage: number | undefined,
  total: number | undefined,
) => {
  if (!usage || !total) return 0;

  return Math.min((usage / total) * 100, 100);
};

export const getActiveSubscriptionWindow = (
  subscriptions?: ResolvedSubscription[],
) => {
  if (!subscriptions || subscriptions.length === 0) {
    return { startDate: undefined, endDate: undefined };
  }

  const now = Date.now();
  const activeSubscriptions = subscriptions.filter((sub) => {
    return !sub.expiry || sub.expiry * 1000 > now;
  });

  if (activeSubscriptions.length === 0) {
    return { startDate: undefined, endDate: undefined };
  }

  // Find min start date and max end date across active subscriptions
  let minStart: number | undefined;
  let maxEnd: number | undefined;

  activeSubscriptions.forEach((sub) => {
    // subscription's Start Date is 'started' if set, otherwise 'created' date
    const subStartDate =
      sub.started && sub.started > 0 ? sub.started : sub.created;

    // subscription's End Date is 'expiry' if set
    const subEndDate = sub.expiry && sub.expiry > 0 ? sub.expiry : undefined;

    if (minStart === undefined || subStartDate < minStart) {
      minStart = subStartDate;
    }

    if (subEndDate && (maxEnd === undefined || subEndDate > maxEnd)) {
      maxEnd = subEndDate;
    }
  });

  return { startDate: minStart, endDate: maxEnd };
};

export const getUpgradeTemplatesUsage = (
  upgradeTemplates: UpgradeTemplate[],
  subscriptions?: ResolvedSubscription[],
) => {
  const activeSubscriptionWindow = getActiveSubscriptionWindow(subscriptions);

  // filter upgrade templates that are published outside of active subscription window
  const templatesUsed = upgradeTemplates.filter(
    (upgradeTemplate) =>
      upgradeTemplate?.first_published &&
      activeSubscriptionWindow.startDate &&
      upgradeTemplate.first_published / 1000 >
        activeSubscriptionWindow.startDate &&
      (!activeSubscriptionWindow.endDate ||
        upgradeTemplate.first_published / 1000 <
          activeSubscriptionWindow.endDate),
  );

  const templatesUsedByResources =
    filterUpgradeTemplatesByResources(templatesUsed);

  return {
    totalCount: templatesUsed.length,
    addonTemplatesCount: templatesUsedByResources.addonUpgradeTemplates.length,
    clusterTemplatesCount:
      templatesUsedByResources.clusterUpgradeTemplates.length,
  };
};

export const getUpgradePlansCount = (
  upgradePlans: UpgradePlan[],
  subscriptions?: ResolvedSubscription[],
) => {
  const activeSubscriptionWindow = getActiveSubscriptionWindow(subscriptions);
  const plansUsed = upgradePlans.filter(
    (upgradePlan) =>
      upgradePlan?.first_published &&
      activeSubscriptionWindow.startDate &&
      upgradePlan.first_published / 1000 > activeSubscriptionWindow.startDate &&
      (!activeSubscriptionWindow.endDate ||
        upgradePlan.first_published / 1000 < activeSubscriptionWindow.endDate),
  );

  const plansUsedByResources = filterUpgradePlansByResources(plansUsed);

  return {
    totalCount: plansUsed.length,
    addonPlansCount: plansUsedByResources.addonUpgradePlans.length,
    clusterPlansCount: plansUsedByResources.clusterUpgradePlans.length,
  };
};

export function ExpiryDisplay(expiryTimestamp: number | undefined): string {
  // Check if expiry date is in the past and return a string with the number of days/months/years since expiry
  // If the string is empty, the expiry date is either in the future or undefined
  if (!expiryTimestamp) return '';
  const currentDate = new Date();
  const expiryDate = fromUnixTime(expiryTimestamp);

  if (isBefore(expiryDate, currentDate)) {
    const daysUntilExpiry = Math.abs(differenceInDays(expiryDate, currentDate));

    if (daysUntilExpiry < 30) {
      return `expired ${daysUntilExpiry} day${
        daysUntilExpiry !== 1 ? 's' : ''
      } ago`;
    } else {
      const yearsUntilExpiry = Math.abs(
        differenceInYears(expiryDate, currentDate),
      );
      const monthsUntilExpiry = Math.abs(
        differenceInMonths(expiryDate, currentDate),
      );

      if (yearsUntilExpiry > 0) {
        return `expired ${yearsUntilExpiry} year${
          yearsUntilExpiry !== 1 ? 's' : ''
        } ago`;
      } else {
        return `expired ${monthsUntilExpiry} month${
          monthsUntilExpiry !== 1 ? 's' : ''
        } ago`;
      }
    }
  } else {
    return '';
  }
}
